import { cn } from '@/utils/cn';

/**
 * Matches GraphQL fragment return type for responsive images.
 * All fields are nullable as returned from GraphQL.
 */
export interface ResponsiveImageData {
    id?: string | null;
    urlSmall?: string | null;
    urlMedium?: string | null;
    urlLarge?: string | null;
    widthSmall?: number | null;
    widthMedium?: number | null;
    widthLarge?: number | null;
    heightSmall?: number | null;
    heightMedium?: number | null;
    heightLarge?: number | null;
    alt?: string | null;
}

/**
 * Props for ResponsiveImage component.
 *
 * @param decorative - If true, forces empty alt text regardless of CMS content.
 *                     Use for purely decorative images (backgrounds, design elements).
 * @param fallbackAlt - Alt text to use when CMS doesn't provide alt text.
 *                      Ignored if decorative is true.
 * @param fill - If true, image will fill its container using h-full instead of h-auto.
 */
interface ResponsiveImageProps {
    image: ResponsiveImageData;
    sizes: string;
    className?: string;
    priority?: boolean;
    fallbackAlt?: string;
    decorative?: boolean;
    fill?: boolean;
}

export default function ResponsiveImage({
    image,
    sizes,
    className,
    priority = false,
    fallbackAlt = '',
    decorative = false,
    fill = false,
}: ResponsiveImageProps) {
    // Guard against missing required fields
    if (!image.urlMedium || !image.widthMedium || !image.heightMedium) {
        return null;
    }

    // Decorative images always get empty alt, regardless of CMS content
    const altText = decorative ? '' : image.alt || fallbackAlt;

    return (
        <img
            srcSet={`${image.urlSmall || image.urlMedium} ${image.widthSmall || image.widthMedium}w, ${image.urlMedium} ${image.widthMedium}w, ${image.urlLarge || image.urlMedium} ${image.widthLarge || image.widthMedium}w`}
            sizes={sizes}
            src={image.urlMedium}
            alt={altText}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            width={image.widthMedium}
            height={image.heightMedium}
            className={cn(fill ? 'h-full w-full' : 'h-auto w-full', className)}
        />
    );
}
